const COMBINING_MARKS = /[̀-ͯ]/g;

/**
 * EDITORIAL SLUG -> the slug the live catalogue actually produces.
 *
 * A product's canonical url is `slugify(its catalogue name)`, so it moves if staff rename the product.
 * The editorial crop programmes (data/crops.ts) and any url already handed out or indexed refer to it
 * by the name it had then. Both must keep resolving.
 *
 * This is not cosmetic. C4L Biofoliar is recorded in the live catalogue as "Crop4Life BIOFOLIAR",
 * which slugifies to `crop4life-biofoliar`; five crop pages asked for `c4l-biofoliar`, got nothing,
 * and silently dropped the foliar product from the grape, pomegranate, citrus, tomato and chilli
 * programmes. `npm run check:seo` now fails if any crop's product slug stops resolving.
 *
 * Add an entry when a product is renamed — never delete one, or you break a live url.
 */
export const PRODUCT_SLUG_ALIASES: Record<string, string> = {
  'c4l-biofoliar': 'crop4life-biofoliar',
  'crop-4-life-biofoliar': 'crop4life-biofoliar',
  'natures-green-4-plus': 'nature-s-green-4-plus',
};

/** Resolve a requested slug to the catalogue's own slug (identity when there is no alias). */
export function canonicalProductSlug(slug: string): string {
  const key = String(slug ?? '').trim().toLowerCase();
  return PRODUCT_SLUG_ALIASES[key] ?? key;
}

/**
 * Every spelling that names the same product — the requested slug, what it aliases to, and anything
 * that aliases to it. Used when resolving against a catalogue, so a link works whichever spelling the
 * database happens to hold.
 */
export function productSlugCandidates(slug: string): string[] {
  const key = String(slug ?? '').trim().toLowerCase();
  const out = new Set<string>([key]);
  const forward = PRODUCT_SLUG_ALIASES[key];
  if (forward) out.add(forward);
  for (const [from, to] of Object.entries(PRODUCT_SLUG_ALIASES)) {
    if (to === key || (forward && to === forward)) out.add(from);
  }
  return [...out];
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .replace(/\+/g, ' plus ')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

/** Hex → "r, g, b" for rgba() interpolation in inline styles. */
export function hexToRgb(hex: string): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, '$1$1') : h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/** Build a low-opacity accent rgba string. */
export function accentAlpha(hex: string, alpha: number): string {
  return `rgba(${hexToRgb(hex)}, ${alpha})`;
}

/** Darken a hex colour by a 0–1 factor. */
export function darken(hex: string, factor: number): string {
  const [r, g, b] = hexToRgb(hex).split(', ').map(Number);
  const d = (c: number) => Math.round(c * (1 - factor));
  return `#${[d(r), d(g), d(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** A contrast-safe version of an accent colour for use as TEXT on light backgrounds. */
export function onLight(hex: string): string {
  return darken(hex, 0.35);
}

function relLuminance(r: number, g: number, b: number): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/**
 * A contrast-safe version of an accent colour for TEXT/icons on the dark (night) cards.
 * Lightens toward white until it clears ~4.5:1 on the near-black card background.
 */
export function onDark(hex: string): string {
  const [r0, g0, b0] = hexToRgb(hex).split(', ').map(Number);
  let r = r0;
  let g = g0;
  let b = b0;
  let t = 0;
  while (relLuminance(r, g, b) < 0.34 && t < 0.9) {
    t += 0.06;
    r = Math.round(r0 + (255 - r0) * t);
    g = Math.round(g0 + (255 - g0) * t);
    b = Math.round(b0 + (255 - b0) * t);
  }
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}
